import classNames from "classnames";
import * as React from "react";
import { useMemo } from "react";
import { useGetColorOfContent } from "~/sharedComponents/colorCoding/useGetColorOfContent";
import { Qtip, showOnHoverAndClick } from "~/sharedComponents/tooltip";
import * as styles from "./DefaultDropdownTrigger.less";
import { withColoredBackground } from "./utils";
import { DefaultDropdownTriggerProps } from ".";

type Props = Pick<
    DefaultDropdownTriggerProps,
    | "theme"
    | "maxWidth"
    | "showValidation"
    | "showWarning"
    | "isDisabled"
    | "tabIndex"
    | "backgroundColor"
    | "testId"
    | "disabledReason"
>;

export const DefaultDropdownTriggerWrapper = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>(
    (
        {
            theme,
            maxWidth,
            showValidation,
            showWarning,
            isDisabled,
            disabledReason,
            children,
            tabIndex,
            backgroundColor,
            testId,
        },
        ref
    ) => {
        const withBackgroundColor = useMemo(
            () => withColoredBackground(theme) && !!backgroundColor,
            [theme, backgroundColor]
        );

        const color = useGetColorOfContent(backgroundColor);

        const className = classNames(
            styles.select_trigger,
            styles[theme],
            showValidation && styles.error,
            showWarning && styles.warning,
            isDisabled && styles.disabled,
            withBackgroundColor && styles.colored
        );

        return (
            <>
                <div
                    ref={ref}
                    role="combobox"
                    data-testid={`default-dropdown-trigger-${testId ?? ""}`}
                    className={className}
                    style={{
                        maxWidth,
                        backgroundColor: withBackgroundColor && backgroundColor,
                        color: withBackgroundColor && color,
                    }}
                    tabIndex={tabIndex}
                >
                    {children}
                </div>
                {isDisabled && !!disabledReason && (
                    <Qtip {...showOnHoverAndClick}>
                        <div className={classNames(styles.select_trigger, styles[theme], styles.disabled_tooltip)} />
                        <>{disabledReason}</>
                    </Qtip>
                )}
            </>
        );
    }
);
